import { useNavigate } from "react-router-dom";
import { ExternalLink, FolderOpen, MessageSquare, Plus, FileText, ArrowRight } from "lucide-react";
import type { AnchorHTMLAttributes } from "react";

/**
 * Action Link protocol: app://action/param
 *
 * Supported actions:
 *  - app://navigate/projects                    → go to projects list
 *  - app://navigate/projects/new                → go to new project page
 *  - app://navigate/projects/{id}               → go to project detail
 *  - app://navigate/projects/{id}?openVariation={vid} → open project + comments modal
 *  - app://navigate/dashboard                   → go to dashboard
 *  - app://navigate/settings                    → go to settings
 *  - app://navigate/activity                    → go to activity logs
 */

interface ActionLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href?: string;
  children?: React.ReactNode;
}

/** Map of action patterns to icons */
function getActionIcon(action: string): React.ReactNode {
  if (action.includes("projects/new")) return <Plus size={14} />;
  if (action.includes("openVariation")) return <MessageSquare size={14} />;
  if (action.includes("projects/")) return <FolderOpen size={14} />;
  if (action.includes("projects")) return <FolderOpen size={14} />;
  if (action.includes("activity")) return <FileText size={14} />;
  return <ArrowRight size={14} />;
}

export function ActionLinkRenderer({ href, children, ...props }: ActionLinkProps) {
  const navigate = useNavigate();

  // Only intercept app:// protocol links
  if (!href || !href.startsWith("app://")) {
    // Regular link: open in new tab for safety
    return (
      <a {...props} href={href} target="_blank" rel="noopener noreferrer" className="chat-md-link">
        {children}
        <ExternalLink size={12} style={{ marginLeft: 4, verticalAlign: "middle" }} />
      </a>
    );
  }

  // Parse app:// protocol
  // Format: app://navigate/path/to/page?query=params
  const actionPath = href.replace("app://", "");
  const [action, ...rest] = actionPath.split("/");

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (action === "navigate") {
      // Reconstruct the path: /rest[0]/rest[1]/...
      const targetPath = "/" + rest.join("/");
      navigate(targetPath);
    }
  };

  const icon = getActionIcon(actionPath);

  return (
    <button
      className="chat-action-link"
      onClick={handleClick}
      type="button"
      title={`Open: ${rest.join("/")}`}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}
